'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { BlogPost } from '@/types/blog';
import { getPosts } from '@/app/blog/actions';

export default function FeaturedBlogPosts() {
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchFeaturedPosts() {
      try {
        setLoading(true);
        const allPosts = await getPosts();
        // Get the 3 most recent posts
        const recent = allPosts.slice(0, 3);
        setFeaturedPosts(recent);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching featured blog posts:', error);
        setLoading(false);
      }
    }

    fetchFeaturedPosts();
  }, []);

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (featuredPosts.length === 0) {
    return null; // No posts to display
  }

  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Latest from our Blog
          </h2>
          <p className="mt-2 text-lg leading-8 text-gray-600">
            Insights on automating text-heavy manual processes with AI
          </p>
        </div>
        
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {featuredPosts.map((post) => (
            <article key={post.slug} className="flex flex-col items-start">
              <div className="flex items-center gap-x-4 text-xs">
                <time dateTime={post.date} className="text-gray-500">
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
                {post.categories && post.categories[0] && (
                  <span className="relative z-10 rounded-full bg-gray-100 px-3 py-1.5 font-medium text-gray-600">
                    {post.categories[0]}
                  </span>
                )}
              </div>
              
              <div className="group relative">
                <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-blue-600">
                  <Link href={`/blog/${post.slug}`}>
                    <span className="absolute inset-0" />
                    {post.title}
                  </Link>
                </h3>
                <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                  {post.excerpt}
                </p>
              </div>
              
              <div className="relative mt-8 flex items-center gap-x-4">
                <div className="text-sm leading-6">
                  <p className="font-semibold text-gray-900">
                    {post.author}
                  </p>
                  {post.readingTime && (
                    <p className="text-gray-600">{post.readingTime}</p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
        
        <div className="mt-10 text-center">
          <Link
            href="/blog"
            className="text-sm font-semibold leading-6 text-blue-600 hover:text-blue-500"
          >
            View all articles <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
} 